import { Fragment } from "react";
import {
  Button,
  Select,
  SelectItem,
  Tag,
  TextArea,
  TextInput,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import i18next from "../../../i18n";
import { TO_BE_AGREED, MEASUREMENT_UNIT_LIST } from "../../../utils";
import { MaterialType } from "../../../types";
import {
  Currency,
  CURRENCY_OPTIONS,
} from "../../../types/purchaseRequestV2.type";
import PeriodDate from "./PeriodDate/PeriodDate";
import LineFilesUploader, {
  LineFile,
} from "./LineFilesUploader/LineFilesUploader";
import { ORDER_TYPES } from "../../../types";
import { SiteResponse } from "../../../types/site.type";
import { OrderToSubmitRequest } from "../../../types";
import ServiceDetailCard from "./ServiceDetailCard/ServiceDetailCard";

const req = <span className="order-required"> *</span>;

type PriceDecrementPaymentProps = {
  type: string;
  materialsToBid: any;
  handlePriceDecrementChanges: any;
  handleSupplyPeriod: any;
  sites?: SiteResponse[];
  handleSiteChange?: (lineId: string, siteId: string) => void;
  handleAddressChange?: (lineId: string, address: string) => void;
  onCreateSite?: (lineId: string) => void;
  onApplyDatesToAll?: (matToBid: any) => void;
  onAddLineFile?: (lineId: string, file: LineFile) => void;
  onRemoveLineFile?: (lineId: string, index: number) => void;
  onServiceDetailChange?: (lineId: string, patch: Partial<OrderToSubmitRequest>) => void;
  showCompany?: boolean;
};

/**
 * Step 4: per-material config card (name/code, service fields, quantity,
 * supply period, and — for inverse bidding — starting price + decrement).
 * Carbon fields (zero Tailwind). Callback signatures unchanged so the launch
 * flow AND Task 6's OrderByRequest drive it identically.
 */
const PriceDecrementPayment = ({
  type,
  materialsToBid,
  handlePriceDecrementChanges,
  handleSupplyPeriod,
  sites,
  handleSiteChange,
  handleAddressChange,
  onCreateSite,
  onApplyDatesToAll,
  onAddLineFile,
  onRemoveLineFile,
  onServiceDetailChange,
  showCompany,
}: PriceDecrementPaymentProps) => {
  const hasMultipleLines = materialsToBid?.length > 1;
  return (
    <Fragment>
      {materialsToBid.map((matToBid: any) => {
        // SCRUM-117: a service is described with the v2 editor, which carries its own name, place,
        // date, currency and files; the per-field blocks below are left to goods.
        const v2Service =
          !!onServiceDetailChange && matToBid.type === MaterialType.SERVICES;
        return (
        <div key={matToBid.id} className="order-line-card">
          <div className="order-line-card__head">
            <p className="order-line-card__title">
              {matToBid.globalMaterialName}
              <span className="order-materials__place">
                {" - "}({matToBid.deliveryPlace?.name ?? TO_BE_AGREED})
              </span>
            </p>
            {showCompany && matToBid.company?.name && (
              <Tag type="gray" size="sm">
                {matToBid.company.name}
              </Tag>
            )}
          </div>

          {v2Service && (
            <ServiceDetailCard
              line={matToBid}
              single={!hasMultipleLines}
              onChange={onServiceDetailChange}
              sites={sites}
              handleSiteChange={handleSiteChange}
              onCreateSite={onCreateSite}
            />
          )}

          {!v2Service && handleSiteChange && matToBid.type === MaterialType.SERVICES && (
            <div className="order-col-full">
              <div className="order-inline-end">
                <Select
                  id={`site-${matToBid.id}`}
                  labelText={i18next.t("tenders.form.servicePlace")}
                  value={matToBid.deliveryPlace?.id ?? ""}
                  onChange={(e) => handleSiteChange(matToBid.id, e.target.value)}
                >
                  <SelectItem value="" text={TO_BE_AGREED} />
                  {sites?.map((site) => (
                    <SelectItem key={site.id} value={site.id} text={site.name} />
                  ))}
                </Select>
                {onCreateSite && (
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={Add}
                    onClick={() => onCreateSite(matToBid.id)}
                  >
                    {i18next.t("sites.add")}
                  </Button>
                )}
              </div>
              {!matToBid.deliveryPlace?.id && handleAddressChange && (
                <TextInput
                  id={`address-${matToBid.id}`}
                  labelText={i18next.t("orderByRequest.manualAddressLabel")}
                  placeholder={i18next.t("orderByRequest.manualAddressPlaceholder")}
                  value={matToBid.address || ""}
                  onChange={(e) =>
                    handleAddressChange(matToBid.id, e.target.value)
                  }
                />
              )}
            </div>
          )}

          <div className="order-grid">
            {!v2Service && (type === ORDER_TYPES.NORMAL || type === ORDER_TYPES.INVERSE) && (
              <>
                <TextInput
                  id="name"
                  labelText={<>{i18next.t("tenders.form.name")}{req}</>}
                  value={matToBid.name ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "name")
                  }
                />
                <TextInput
                  id="internalCode"
                  labelText={i18next.t("tenders.form.internalCode")}
                  value={matToBid.internalCode ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "internalCode")
                  }
                />
              </>
            )}

            {!v2Service && matToBid.type === MaterialType.SERVICES && (
              <>
                <div className="order-col-full">
                  <TextArea
                    id="purpose"
                    rows={4}
                    maxLength={1000}
                    labelText={<>{i18next.t("tenders.form.purpose")}{req}</>}
                    value={matToBid.purpose ?? ""}
                    onChange={(e) =>
                      handlePriceDecrementChanges(e, matToBid, "purpose")
                    }
                  />
                </div>
                <div className="order-col-full">
                  <TextInput
                    id="brand"
                    labelText={i18next.t("newMaterialForm.brand")}
                    value={matToBid.brand ?? ""}
                    onChange={(e) =>
                      handlePriceDecrementChanges(e, matToBid, "brand")
                    }
                  />
                </div>
                <Select
                  id="measurementUnit"
                  labelText={i18next.t("newMaterialForm.measurementUnit")}
                  value={matToBid.measurementUnit ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "measurementUnit")
                  }
                >
                  <SelectItem
                    value=""
                    text={i18next.t("newMaterialForm.selectMeasurementUnit")}
                  />
                  {MEASUREMENT_UNIT_LIST?.map((unit, index) => (
                    <SelectItem
                      key={`${unit.name}-${index}`}
                      value={unit.name}
                      text={unit.name}
                    />
                  ))}
                </Select>
                <Select
                  id={`currency-${matToBid.id}`}
                  labelText={i18next.t("orderByRequest.itemCurrencyLabel")}
                  value={matToBid.coin ?? Currency.USD}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "coin")
                  }
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} text={opt.label} />
                  ))}
                </Select>
              </>
            )}

            {!v2Service && (
              <div className="order-inline-end">
                <TextInput
                  id="volume"
                  type="number"
                  min={0}
                  labelText={
                    <>
                      {i18next.t("tenders.quantity")}
                      {matToBid.type !== MaterialType.SERVICES && req}
                    </>
                  }
                  value={matToBid.quantity ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "quantity")
                  }
                />
                {matToBid.type !== MaterialType.SERVICES && (
                  <span className="order-unit">{matToBid.measurementUnit}</span>
                )}
              </div>
            )}

            {!v2Service &&
              type !== ORDER_TYPES.JOINT &&
              type !== ORDER_TYPES.INVERSE_JOINT && (
                <>
                  <PeriodDate
                    matToBid={matToBid}
                    handleSupplyPeriod={handleSupplyPeriod}
                  />
                  {onApplyDatesToAll && hasMultipleLines && (
                    <div className="order-col-full">
                      <Button
                        kind="ghost"
                        size="sm"
                        onClick={() => onApplyDatesToAll(matToBid)}
                      >
                        {i18next.t("tenders.form.applyDateToAll")}
                      </Button>
                    </div>
                  )}
                </>
              )}

            {(type === ORDER_TYPES.INVERSE ||
              type === ORDER_TYPES.INVERSE_JOINT) && (
              <Fragment>
                <TextInput
                  id="startingPrice"
                  type="number"
                  min={0}
                  labelText={<>{i18next.t("tenders.initialPrice")}{req}</>}
                  value={matToBid.initialPrice ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "initialPrice")
                  }
                />
                <TextInput
                  id="decrement"
                  type="number"
                  min={0}
                  labelText={<>{i18next.t("tenders.minimumDecrement")}{req}</>}
                  value={matToBid.minimumDecrement ?? ""}
                  onChange={(e) =>
                    handlePriceDecrementChanges(e, matToBid, "minimumDecrement")
                  }
                />
              </Fragment>
            )}

            {!v2Service &&
              onAddLineFile &&
              onRemoveLineFile &&
              matToBid.type === MaterialType.SERVICES && (
                <LineFilesUploader
                  files={(matToBid.files ?? []) as LineFile[]}
                  onAdd={(file) => onAddLineFile(matToBid.id, file)}
                  onRemove={(index) => onRemoveLineFile(matToBid.id, index)}
                />
              )}
          </div>
        </div>
        );
      })}
    </Fragment>
  );
};

export default PriceDecrementPayment;
